// Build a semantic-mask edit prompt for object replacement (Phase 9-3).
// Gemini's image-edit pipeline does not accept binary masks — instead it
// does conversational region edits ("change only the X to a Y, keep
// everything else identical"). We just stitch the user's two short fields
// into that template along with strong preservation language.
//
// Throws on missing inputs so the caller knows to surface a validation
// error to the user before burning a credit.

export function buildEditPrompt(target, replacement) {
  const t = (target || '').trim();
  const r = (replacement || '').trim();
  if (!t) throw new Error('TARGET_REQUIRED');
  if (!r) throw new Error('REPLACEMENT_REQUIRED');
  return `Edit the provided interior space photo. Change ONLY ${t} — replace it with ${r}.

STRICT PRESERVATION REQUIREMENTS:
- Keep all walls, floor, ceiling, windows, and doors EXACTLY as in the original
- Keep all OTHER furniture and decor untouched (only the specified target may change)
- Keep the same camera angle, perspective, and lighting
- Match the new item's style, color, and proportions to the existing room
- Add nothing the request does not ask for — no people, pets, text, or objects that are not in the original photo, unless the request itself asks for them

Generate the edited image.`;
}

// Overlay-based edit: image 1 is the source, image 2 is the SAME source
// with a solid red fill burned over the region the user wants to change.
// Gemini interprets "red painted region" reliably while it ignores abstract
// black/white masks.
export function buildEditPromptWithOverlay(replacement) {
  const r = (replacement || '').trim();
  if (!r) throw new Error('REPLACEMENT_REQUIRED');
  return `You are given TWO images of the same interior scene:
- Image 1: the source room photo (clean, no markings).
- Image 2: the SAME room with a solid RED region painted on top — that red marking shows the area the user wants to change.

Produce an edited version of Image 1 where ONLY the area corresponding to the red region in Image 2 is replaced with: ${r}.

STRICT PRESERVATION REQUIREMENTS:
- Pixels OUTSIDE the red region must remain identical to Image 1 — same walls, floor, ceiling, windows, doors, decor, furniture, lighting, camera angle, perspective.
- Inside the red region, render the new item naturally — match the room's lighting, perspective, scale, and visual style. The new item should look like it has always been there.
- Add nothing the request does not ask for — no people, pets, text, or objects that are not in the original photo, unless the request itself asks for them.
- Do NOT output the red highlight. Output a clean photograph of the edited room at full resolution.`;
}

// 브러시 없이 말로만 하는 편집 (2.3.0) — "현관 옆에 동상 추가" 처럼 무엇을 없애거나
// 바꾸는 게 아니라 지금 사진에 더하는 요청도 있다. 영역 지정 없이 요청대로만 고친다.
export function buildInstructionEditPrompt(instruction) {
  const r = (instruction || '').trim();
  if (!r) throw new Error('REPLACEMENT_REQUIRED');
  return `Edit the provided photo of a space according to this request: ${r}

STRICT PRESERVATION REQUIREMENTS:
- Change only what the request asks for. Everything else — walls, floor, ceiling, windows, doors, furniture, decor, lighting, camera angle, perspective — stays EXACTLY as in the original.
- Anything added must match the scene's lighting, perspective, scale, and visual style, as if it had always been there.
- Add nothing the request does not ask for — no people, pets, text, or objects that are not in the original photo, unless the request itself asks for them.

Generate the edited image.`;
}
